import { ConceptPageCredentials, KeyValueStateContainer, RegisteredModels, UrlResolverContainer, UserContext, channel } from "@tm/models"
import { Container } from "@tm/nexus"
import { create } from "zustand"
import * as Data from "../../../data"
import { GetStreetsRequest, SetDutchOrdersMDMParamsRequest } from "../../../data/model"
import { UserSettingsState } from "./model"

export * from "./model"

const DEFAULT_STATE: UserSettingsState = {
    addressSuggestionsLoading: false,
    subUsersLoading: false,
    userAdminConfig: {
        adminCanEditUserPassword: false,
    },
    openUserSettings: false,
    isFastScroll: false,
    editProfileModalOpen: false,
}

type UserSettingsStore = {
    state: UserSettingsState
    actions: {
        loadAddressSuggestions: (street?: string, zip?: string, city?: string) => Promise<void>
        resetAddressSuggestions: () => void
        showSubUsers: (languageId: string) => Promise<void>
        saveConceptPageCredentials: (credentials: ConceptPageCredentials) => Promise<void>
        checkConceptPage: (userContext?: UserContext) => Promise<void>
        changeAnyway: () => void
        setDutchOrdersMDMParams: (request: SetDutchOrdersMDMParamsRequest) => Promise<void>
        setOpenOrToggleUserSettings: (value?: boolean) => void
        setScrollTo: (location?: string) => void
        setFastScroll: (value: boolean) => void
        setEditProfileModalOpen: (isOpen: boolean, closeSettingsOnCloseEditProfile?: boolean) => void
    }
}

export const userSettingsStore = create<UserSettingsStore>((set, get) => ({
    state: { ...DEFAULT_STATE },
    actions: {
        loadAddressSuggestions: async (street, zip, city) => {
            const request: GetStreetsRequest = {
                countryCode: "DE",
                latitude: 53.720903,
                longitude: 10.281732,
                postcode: zip,
                region: city,
                street,
            }

            set((prev) => ({ state: { ...prev.state, addressSuggestionsLoading: true } }))

            Data.getStreets(request).then((list) =>
                set((prev) => ({ state: { ...prev.state, addressSuggestions: list, addressSuggestionsLoading: false } }))
            )
        },

        resetAddressSuggestions: () => {
            set((prev) => ({ state: { ...prev.state, addressSuggestions: DEFAULT_STATE.addressSuggestions } }))
        },

        showSubUsers: async (languageId) => {
            set((prev) => ({ state: { ...prev.state, subUsersLoading: true } }))
            Data.showSubUsers(languageId).then((response) =>
                set((prev) => ({
                    state: {
                        ...prev.state,
                        subUsersLoading: false,
                        subUsers: response?.subUsers,
                        userAdminConfig: response?.userAdminConfig || get().state.userAdminConfig,
                        subUsersPermissionContainer: response?.permissions,
                    },
                }))
            )
        },

        saveConceptPageCredentials: async (credentials) => {
            set((prev) => ({ state: { ...prev.state, conceptPage: false, conceptPageLoading: true } }))
            const container = Container.getInstance(RegisteredModels.KeyValueStore) as KeyValueStateContainer
            const jsonCreds = JSON.stringify(credentials)
            container
                .action("saveKeyValue")("CONCEPT_PAGE_CREDENTIALS", jsonCreds, true)
                .then(() => {
                    set((prev) => ({ state: { ...prev.state, conceptPageLoading: false, conceptPageUpdated: true } }))
                    channel("GLOBAL").publish("CONCEPT_PAGE/CREDENTIALS_CHANGED", credentials)
                })
        },

        checkConceptPage: async (userContext) => {
            const { state } = get()
            if (state.conceptPage != null || state.conceptPageLoading) {
                return
            }
            if (
                userContext &&
                userContext.externalModules &&
                userContext.externalModules.some(
                    (extModule) =>
                        extModule.parameter && extModule.parameter.some((param) => param.key === "DocumentTypeID" && param.value === "ConceptPage")
                )
            ) {
                set((prev) => ({ state: { ...prev.state, conceptPage: false, conceptPageLoading: true } }))
                const container = Container.getInstance(RegisteredModels.UrlResolver) as UrlResolverContainer
                container
                    .action("getConceptPageUrl")()
                    .then((result) => {
                        set((prev) => ({
                            state: {
                                ...prev.state,
                                conceptPage: !!result.canChangeCredentials,
                                conceptPageHasCredentials: !!result.hasCredentials,
                                conceptPageLoading: false,
                            },
                        }))
                    })
            }
        },

        changeAnyway: () => {
            set((prev) => ({
                state: {
                    ...prev.state,
                    conceptPageHasCredentials: false,
                    conceptPageLoading: false,
                },
            }))
        },

        setDutchOrdersMDMParams: async (request) => {
            set((prev) => ({ state: { ...prev.state, rdwCompanyInfoLoading: true } }))
            Data.setDutchOrdersMDMParams(request).then(
                () => set((prev) => ({ state: { ...prev.state, rdwCompanyInfoLoading: false, rdwCompanyInfoLoaded: true } })),
                () => set((prev) => ({ state: { ...prev.state, rdwCompanyInfoLoading: false, rdwCompanyInfoError: true } }))
            )
        },

        setOpenOrToggleUserSettings: (value) => {
            set((prev) => ({
                state: {
                    ...prev.state,
                    openUserSettings: value ?? !prev.state.openUserSettings,
                    isFastScroll: value === false || (value === undefined && prev.state.openUserSettings) ? false : prev.state.isFastScroll,
                },
            }))
        },

        setScrollTo: (location) => {
            set((prev) => ({ state: { ...prev.state, scrollTo: location } }))
        },

        setFastScroll: (value) => {
            set((prev) => ({ state: { ...prev.state, isFastScroll: value } }))
        },

        setEditProfileModalOpen: (isOpen, closeSettingsOnCloseEditProfile) => {
            set((prev) => ({ state: { ...prev.state, editProfileModalOpen: isOpen, closeSettingsOnCloseEditProfile } }))
        },
    },
}))

export const useUserSettingsStore = <T>(selector: (state: UserSettingsStore) => T): T => {
    return userSettingsStore(selector)
}
